import React, {
  useState,
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { formatedLabel } from './filters.utils'
import useMediaQuery from '~/hooks/use-media-query'
import { Box } from 'theme-ui'

export type TFilter = {
  name: string
  value: string | boolean
}
export interface ISelectedFilters {
  [key: string]: TFilter[]
}

export interface IFilterItem {
  name: string
  value: string
  label: string
  categoryName: string
}

export type TChangeItem = { name: string; value: string | boolean } | null
export type FilterType = {
  inStockOnly?: boolean
  price?: any
  selectedFilters?: Record<string, TFilter[]>
}

interface IFilterContext {
  selectedFilters?: ISelectedFilters
  setSelectedFilters: React.Dispatch<React.SetStateAction<ISelectedFilters | undefined>>
  inStockOnlyBool: boolean
  setInStockOnlyBool: (inStockOnly: boolean) => void
  infiniteScrollRef?: React.RefObject<InfiniteScroll> | null
  toggleValue: (changeItem: TChangeItem, categoryName: string) => void
  selectedValuesArray?: IFilterItem[]
  showFilter: boolean
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>
  isMobile?: boolean
  isTablet?: boolean
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  currentFilters: FilterType | undefined
  setCurrentFilters: React.Dispatch<React.SetStateAction<FilterType | undefined>>
  Top: any
  scrollToTop: () => void
  topRef?: React.RefObject<HTMLDivElement> | null
}

const FilterContext = createContext<IFilterContext>({
  selectedFilters: {},
  setSelectedFilters: () => null,
  inStockOnlyBool: false,
  setInStockOnlyBool: () => false,
  infiniteScrollRef: null,
  toggleValue: () => null,
  selectedValuesArray: [],
  showFilter: true,
  setShowFilter: () => true,
  isMobile: false,
  isLoading: false,
  setIsLoading: () => false,
  currentFilters: {},
  setCurrentFilters: () => null,
  Top: <div />,
  scrollToTop: () => null,
  topRef: null,
})

export const useFilterContext = () => useContext(FilterContext)

const parseBreakpoint = (breakpoint: string) => {
  return parseInt(breakpoint.replace('rem', '')) * 16 - 1
}

const FilterProvider = ({ children }: { children: React.ReactNode }) => {
  const infiniteScrollRef = useRef<React.ClassAttributes<InfiniteScroll> & InfiniteScroll>(null)
  const [selectedFilters, setSelectedFilters] = useState<ISelectedFilters | undefined>()
  const [inStockOnlyBool, setInStockOnlyBool] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery({
    query: breakpoints => `(max-device-width: 1024px)`,
  })
  const isTablet = useMediaQuery({
    query: breakpoints => `(max-device-width: ${breakpoints[4]})`,
  })
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [currentFilters, setCurrentFilters] = useState<FilterType | undefined>({})

  const topRef = useRef<HTMLDivElement>(null)
  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  const Top = () =>
    (
      <Box
        ref={topRef}
        sx={{
          height: 0,
          width: '1px',
        }}
      />
    ) as JSX.Element

  const toggleValue = useCallback(
    (changeItem: TChangeItem, categoryName: string) => {
      setIsLoading(true)
      if (typeof window === 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'Filter change',
          value: `${categoryName}:${changeItem?.value || ''}`,
        })
      }
      setSelectedFilters((state: any) => {
        const newState = { ...state }
        if (newState[categoryName]) {
          if (!changeItem) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newState[categoryName]
            return newState
          }
          const index = newState[categoryName].filter(
            (item: { name: string; value: string }) => item.value === changeItem.value
          )
          if (index.length) {
            newState[categoryName] = newState[categoryName].filter(
              (item: { name: string; value: string }) => item.value !== changeItem.value
            )
          } else {
            newState[categoryName].push(changeItem)
          }
          if (!newState[categoryName].length) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newState[categoryName]
          }
        } else {
          newState[categoryName] = [changeItem]
        }
        return newState
      })

      if (infiniteScrollRef?.current) {
        infiniteScrollRef.current.props.next()
      }

      scrollToTop()

      setTimeout(() => {
        setIsLoading(false)
      }, 100)
    },

    [setSelectedFilters, selectedFilters, infiniteScrollRef]
  )

  const selectedValuesArray = useMemo(() => {
    const selectedValues: IFilterItem[] = []
    if (!selectedFilters) return selectedValues
    Object.keys(selectedFilters).forEach(key => {
      selectedFilters[key].forEach((value: any) => {
        selectedValues.push({ ...value, label: formatedLabel(key), categoryName: key })
      })
    })
    return selectedValues
  }, [selectedFilters])

  return (
    <FilterContext.Provider
      value={{
        selectedFilters,
        setSelectedFilters,
        inStockOnlyBool,
        setInStockOnlyBool,
        infiniteScrollRef,
        toggleValue,
        selectedValuesArray,
        showFilter,
        setShowFilter,
        isMobile,
        isTablet,
        isLoading,
        setIsLoading,
        setCurrentFilters,
        currentFilters,
        Top,
        scrollToTop,
        topRef,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}

export default FilterProvider
